import { yupResolver } from '@hookform/resolvers/yup';
import { memo, useCallback, useEffect, useId } from 'react';
import { useForm } from 'react-hook-form';
import { useToggle } from 'react-use';
import { type InferType, object, string } from 'yup';

import { useTrackAddedShareLinkPasscode } from '@air/analytics';
import { type ShortUrlResponse } from '@air/api/types';
import { RectangleEllipsisIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';

import { PasswordInputWithVisibilityToggle } from '~/components/Form/PasswordInputWithVisibilityToggle';
import { OnOffShareToggleRow } from '~/components/ShareModal/components/OnOffShareToggleRow';
import { UpsellFlashIconButton } from '~/components/ShareModal/components/UpsellFlashIconButton';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';
import { useShareSettings } from '~/hooks/useShareSettings';

const PASSCODE_MIN_LENGTH = 8;

export const PasscodeProtectionRowFormValidationSchema = object({
  passcode: string()
    .required('Passcode is required')
    .min(PASSCODE_MIN_LENGTH, `Passcode must be at least ${PASSCODE_MIN_LENGTH} characters.`)
    .matches(/\d+/, 'Passcode must include a number.')
    .matches(/[a-z]/, 'Passcode must include one lowercase letter.')
    .matches(/[A-Z]/, 'Passcode must include one uppercase letter.')
    .default(''),
});

export type PasscodeProtectionRowFormInput = InferType<typeof PasscodeProtectionRowFormValidationSchema>;

export type PasscodeProtectionRowProps = {
  shortIdInfo: ShortUrlResponse;
  onChangePasscode: (passcode: string | null) => Promise<unknown>;
  settingsDisabled?: boolean;
  /**
   * Hide the per-row Save button and instead commit every change to the
   * parent on the fly. Use this when the parent owns a footer CTA that
   * persists everything at once (e.g. the create-collection flow).
   */
  hideSaveButton?: boolean;
};

export const PasscodeProtectionRow = memo(
  ({ shortIdInfo, onChangePasscode, hideSaveButton = false, settingsDisabled = false }: PasscodeProtectionRowProps) => {
    const formId = useId();
    const { passcodeEnabled } = useShareSettings();
    const [isShowingPasscodeForm, showPasscodeForm] = useToggle(!!shortIdInfo.passcode);
    const { trackAddedShareLinkPasscode } = useTrackAddedShareLinkPasscode();

    const {
      formState: { errors, isDirty, isSubmitting, isValid },
      handleSubmit,
      register,
      reset,
      watch,
    } = useForm<PasscodeProtectionRowFormInput>({
      defaultValues: { passcode: shortIdInfo.passcode ?? '' },
      mode: 'all',
      resolver: yupResolver(PasscodeProtectionRowFormValidationSchema),
    });

    const watchedPasscode = watch('passcode');

    const handleToggleChange = useCallback(
      async (checked: boolean) => {
        if (!checked) {
          if (shortIdInfo.passcode && isShowingPasscodeForm) {
            try {
              await onChangePasscode(null);
            } catch {
              // Server-side clear failed — leave the toggle on so the user
              // can see the passcode is still in effect and retry.
              return;
            }
          }
          reset({ passcode: '' });
        }
        showPasscodeForm(checked);
      },
      [shortIdInfo.passcode, isShowingPasscodeForm, onChangePasscode, showPasscodeForm, reset],
    );

    const onSubmit = useCallback(
      async ({ passcode }: PasscodeProtectionRowFormInput) => {
        try {
          // Skip tracking when there's no real shortId — the create flow
          // synthesizes an empty `id` and re-fires this event with the real
          // id after `handleCreate`, so tracking here would duplicate the
          // event with empty data.
          if (shortIdInfo.id) {
            trackAddedShareLinkPasscode({ shortId: shortIdInfo.id });
          }
          await onChangePasscode(passcode || null);
          // Reset to the just-submitted value so the form is no longer dirty
          // and the Save button disables again.
          reset({ passcode });
        } catch {
          // Swallow: the mutation surfaces its own error UI; we just keep the
          // form in its dirty state so the user can retry.
        }
      },
      [trackAddedShareLinkPasscode, shortIdInfo.id, onChangePasscode, reset],
    );

    // Auto-commit mode: when the Save button is hidden, push every dirty
    // change up to the parent so it can include the latest value in its
    // own (footer) submit. Gated on `isDirty` so the initial mount doesn't
    // re-commit the seeded value, and on `isValid` to mirror the Save
    // button's validation check — only valid values are committed.
    useEffect(() => {
      if (!hideSaveButton || !isDirty || !isValid) {
        return;
      }
      void onChangePasscode(watchedPasscode || null);
    }, [hideSaveButton, isDirty, isValid, watchedPasscode, onChangePasscode]);

    const passcodeError = errors.passcode?.message;
    const showError = !!passcodeError && isDirty;

    return (
      <div className="flex flex-col gap-2">
        <OnOffShareToggleRow
          label="Add passcode protection"
          labelAccessory={
            !passcodeEnabled && <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton>
          }
          checked={isShowingPasscodeForm}
          disabled={settingsDisabled || !passcodeEnabled}
          isLoading={false}
          onToggle={handleToggleChange}
          data-testid="PASSCODE_PROTECTION_LABEL"
        />

        {passcodeEnabled && !settingsDisabled && isShowingPasscodeForm && (
          <div>
            <form id={formId} onSubmit={handleSubmit(onSubmit)}>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <PasswordInputWithVisibilityToggle
                    id={`${formId}-passcode`}
                    size="medium"
                    data-testid="PASSCODE_PROTECTION_INPUT"
                    className="pr-8 pl-9"
                    hasError={showError}
                    placeholder="Password"
                    hideLabel="Hide passcode"
                    leadingIcon={
                      <RectangleEllipsisIcon
                        size="sm"
                        aria-hidden="true"
                        className="pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2 ads:text-icon-emphasis text-gray-12"
                      />
                    }
                    showLabel="Show passcode"
                    toggleClassName="absolute top-1/2 right-1 z-10 -translate-y-1/2"
                    toggleTestId="PASSCODE_PROTECTION_VISIBILITY_TOGGLE"
                    wrapperClassName="rounded-8"
                    {...register('passcode')}
                  />
                </div>
                {!hideSaveButton && (
                  <Button
                    appearance="filled"
                    color="blue"
                    data-testid="PASSCODE_PROTECTION_SUBMIT_BUTTON"
                    disabled={!isDirty || !isValid || isSubmitting}
                    isLoading={isSubmitting}
                    size="medium"
                    type="submit"
                  >
                    Save
                  </Button>
                )}
              </div>
            </form>
            {showError && (
              <p className="mb-0 ads:text-error text-12 text-red-9" data-testid="PASSCODE_PROTECTION_INPUT-error">
                {passcodeError}
              </p>
            )}
          </div>
        )}
      </div>
    );
  },
);

PasscodeProtectionRow.displayName = 'PasscodeProtectionRow';
